import { useCallback, useEffect, useRef, useState } from "react";

interface ScrollingState {
  isScrollingDown: boolean;
  isScrollingUp: boolean;
  isScrolling: boolean;
}

interface Options {
  enabled?: boolean;
  optimized?: boolean;
}

/**
 * Custom hook to detect scrolling direction and state.
 *
 * @param {Object} options - Options for the hook.
 * @param {boolean} [options.enabled=true] - Whether the hook is enabled.
 * @param {boolean} [options.optimized=true] - Whether to optimize property access tracking.
 * @returns {ScrollingState} - An object containing scrolling state.
 */

const useIsScrolling = ({
  enabled = true,
  optimized = true,
}: Options = {}): ScrollingState => {
  const [scrolling, setScrolling] = useState<ScrollingState>({
    isScrollingDown: false,
    isScrollingUp: false,
    isScrolling: false,
  });
  const lastScrollTop = useRef(0);
  const ticking = useRef(false);
  const accessedProperties = useRef(new Set<keyof ScrollingState>());

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      window.requestAnimationFrame(() => {
        const currentPosition =
          window.pageYOffset || document.documentElement.scrollTop;

        let isScrollingDown = false;
        let isScrollingUp = false;
        if (currentPosition > 0) {
          isScrollingDown = currentPosition > lastScrollTop.current;
          isScrollingUp = currentPosition < lastScrollTop.current;
        }
        const isScrolling = isScrollingDown || isScrollingUp;

        // Only update if the new values are different from current ones
        const newScrolling = {
          isScrollingDown,
          isScrollingUp,
          isScrolling,
        };

        // Check if any accessed properties have changed
        const hasChanges = optimized
          ? Array.from(accessedProperties.current).some(
              (prop) => newScrolling[prop] !== scrolling[prop],
            )
          : true;

        if (hasChanges) {
          setScrolling(newScrolling);
        }

        lastScrollTop.current = Math.max(0, currentPosition);

        ticking.current = false;
      });
      ticking.current = true;
    }
  }, [optimized, scrolling]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Set initial scroll position
    lastScrollTop.current =
      window.pageYOffset || document.documentElement.scrollTop;

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, enabled]);

  // Create a proxy that tracks property access
  const scrollingState = optimized
    ? new Proxy(scrolling, {
        get(target, property: keyof ScrollingState) {
          // Track which properties are accessed
          if (typeof property === "string" && property in target) {
            accessedProperties.current.add(property);
          }
          return target[property];
        },
      })
    : scrolling;

  return scrollingState;
};

export { useIsScrolling };
